import { Component } from '@theme/component';

export const motionAllowed = (mode, reduced, saveData) => mode === 'video' && !reduced && !saveData;
export const canAdvance = ({ paused, quiet, visible, hidden, focused, intro }) => !paused && !quiet && visible && !hidden && !focused && !intro;

class BarryWorkEntry extends Component {
  reduced = matchMedia('(prefers-reduced-motion: reduce)');
  portrait = matchMedia('(max-aspect-ratio: 1/1)');
  advanceTimer;
  introTimers = [];
  frame;
  controller;
  observer;
  introActive = false;
  connectedCallback() { super.connectedCallback(); this.setup(); }
  updatedCallback() { this.teardown(); super.updatedCallback(); this.setup(); }
  setup() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.slides = [...this.querySelectorAll('[data-slide]')];
    if (!this.slides.length) return;
    this.index = 0;
    this.mode = this.dataset.mode || 'video';
    this.paused = false;
    this.visible = true;
    this.focused = false;
    this.duration = Math.max(4000, Math.min(10000, Number(this.dataset.duration) || 5000));
    this.dialog = this.querySelector('dialog');
    this.controls = this.querySelector('.bc-entry-controls');
    this.toggle = this.querySelector('[data-toggle]');
    this.stills = this.querySelector('[data-stills]');
    this.replay = this.querySelector('[data-replay]');
    if (this.controls) this.controls.hidden = false;
    this.querySelectorAll('[data-select]').forEach(button => button.addEventListener('click', () => this.select(Number(button.dataset.select)), options));
    this.toggle?.addEventListener('click', () => {
      this.paused = !this.paused;
      // An explicit play action overrides the focus hold until focus moves again.
      if (!this.paused) this.focused = false;
      this.sync();
    }, options);
    this.stills?.addEventListener('click', () => { this.mode = this.mode === 'still' ? 'video' : 'still'; this.sync(); }, options);
    this.replay?.addEventListener('click', () => this.startOpening(true), options);
    this.addEventListener('focusin', event => {
      if (event.target.closest('dialog')) return;
      this.focused = true;
      this.sync();
    }, options);
    this.addEventListener('focusout', event => {
      this.focused = Boolean(event.relatedTarget && this.contains(event.relatedTarget) && !event.relatedTarget.closest('dialog'));
      this.sync();
    }, options);
    this.addEventListener('keydown', event => {
      if (event.target.closest('dialog')) return;
      if (event.target.matches('[data-select]') && ['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const i = event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : (this.index + (event.key === 'ArrowRight' ? 1 : -1) + this.slides.length) % this.slides.length;
        this.select(i);
        this.querySelector(`[data-select="${i}"]`)?.focus();
      }
    }, options);
    this.slides.forEach(slide => {
      const video = slide.querySelector('video');
      if (!video) return;
      video.addEventListener('playing', () => { if (slide === this.slides[this.index] && this.running()) slide.classList.add('is-playing'); else video.pause(); }, options);
      video.addEventListener('error', () => slide.classList.remove('is-playing'), options);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.introActive) this.finishOpening();
      this.sync();
    }, options);
    this.reduced.addEventListener('change', () => { if (this.reduced.matches) this.finishOpening(); this.sync(); }, options);
    navigator.connection?.addEventListener('change', () => { if (navigator.connection.saveData) this.finishOpening(); this.sync(); }, options);
    this.portrait.addEventListener('change', () => { this.select(this.index); }, options);
    window.addEventListener('resize', () => {
      this.measure();
      if (this.introActive) this.finishOpening();
    }, options);
    this.observer = new IntersectionObserver(entries => {
      this.visible = entries[0].isIntersecting;
      this.sync();
    }, { threshold: .15 });
    this.observer.observe(this);
    if (this.dialog) {
      this.dialog.querySelector('.bc-entry-skip').addEventListener('click', () => this.finishOpening(), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.finishOpening(); }, options);
      this.dialog.addEventListener('close', () => { if (this.introActive) this.finishOpening(); }, options);
      this.dialog.addEventListener('keydown', event => {
        if (['Escape', 'Enter', ' ', 'ArrowDown', 'PageDown'].includes(event.key)) { event.preventDefault(); this.finishOpening(); }
      }, options);
      this.dialog.addEventListener('click', () => this.finishOpening(), options);
      this.dialog.addEventListener('wheel', () => this.finishOpening(), { ...options, passive: true });
      this.dialog.addEventListener('touchmove', () => this.finishOpening(), { ...options, passive: true });
    }
    this.measure();
    let seen = false;
    try { seen = sessionStorage.getItem('bc-work-entry-v1') === '1'; } catch { /* Optional session memory. */ }
    this.select(0);
    if (this.dataset.intro === 'true' && this.dataset.auto === 'true' && this.dataset.editor !== 'true' && !seen && !location.hash && !this.quiet()) this.startOpening();
    document.documentElement.classList.remove('bc-entry-pending');
  }
  quiet() { return !motionAllowed(this.mode, this.reduced.matches, Boolean(navigator.connection?.saveData)); }
  running() { return canAdvance({ paused: this.paused, quiet: this.quiet(), visible: this.visible, hidden: document.hidden, focused: this.focused, intro: false }); }
  measure() {
    const top = this.getBoundingClientRect().top + window.scrollY;
    if (top > 0 && top < 240) this.style.setProperty('--entry-offset', `${top}px`);
  }
  select(index) {
    if (!Number.isInteger(index) || index < 0 || index >= this.slides.length) return;
    this.index = index;
    this.slides.forEach((slide, i) => {
      const current = i === index;
      slide.classList.toggle('is-active', current);
      slide.setAttribute('aria-hidden', String(!current));
      slide.inert = !current;
      if (current) {
        const img = slide.querySelector('img');
        const source = slide.querySelector('source');
        if (source?.dataset.srcset) source.srcset = source.dataset.srcset;
        if (img?.dataset.src) img.src = img.dataset.src;
      } else slide.querySelector('video')?.pause();
    });
    this.querySelectorAll('[data-select]').forEach((button, i) => {
      button.setAttribute('aria-pressed', String(i === index));
      const s = this.slides[i].querySelector(this.portrait.matches ? '.bc-entry-port' : '.bc-entry-land');
      button.setAttribute('aria-label', (s?.querySelector('span')?.textContent || button.getAttribute('aria-label')) + ', ' + (this.slides[i].dataset.kind === 'render' ? 'render or photo' : 'film'));
    });
    this.sync();
  }
  sync() {
    clearTimeout(this.advanceTimer);
    this.progress?.cancel();
    if (!this.slides?.length) return;
    const quiet = this.quiet();
    this.dataset.quiet = String(quiet);
    const pref = this.reduced.matches || Boolean(navigator.connection?.saveData);
    if (this.stills) {
      this.stills.disabled = pref;
      this.stills.setAttribute('aria-pressed', String(quiet));
    }
    if (this.toggle) {
      this.toggle.disabled = quiet;
      this.toggle.textContent = this.paused ? this.dataset.play : this.dataset.pause;
      this.toggle.setAttribute('aria-pressed', String(this.paused));
    }
    if (this.replay) this.replay.disabled = pref || this.mode === 'still';
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video) {slide.dataset.moving = String(!quiet && this.running() && i === this.index);return;}
      if (quiet) {
        video.pause();
        if (video.hasAttribute('src')) { video.removeAttribute('src'); video.load(); }
        slide.classList.remove('is-playing');
      } else if (i === this.index && this.running()) {
        const source = this.portrait.matches ? video.dataset.portVideo : video.dataset.landVideo;
        if (source && video.getAttribute('src') !== source) { video.src = source; video.load(); slide.classList.remove('is-playing'); }
        if (source) video.play().catch(() => slide.classList.remove('is-playing'));
      } else video.pause();
    });
    this.preloadNext();
    if (this.running() && !this.introActive && this.slides.length > 1) {
      const line = this.querySelector(`[data-select="${this.index}"] .bc-entry-progress`);
      this.progress = line?.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], { duration: this.duration, fill: 'forwards' });
      this.advanceTimer = setTimeout(() => this.select((this.index + 1) % this.slides.length), this.duration);
    }
  }
  preloadNext() {
    if(this.quiet()||!this.running()||this.slides.length<2)return;
    const nextIndex=(this.index+1)%this.slides.length;
    this.slides.forEach((slide,i)=>{const v=slide.querySelector('video');if(v&&i!==this.index)v.preload=i===nextIndex?'auto':'none';});
    const next=this.slides[nextIndex],img=next.querySelector('img'),pictureSource=next.querySelector('source');
    if(pictureSource?.dataset.srcset)pictureSource.srcset=pictureSource.dataset.srcset;
    if(img?.dataset.src){img.loading='eager';img.src=img.dataset.src;}
    const video=next.querySelector('video');
    if(!video)return;
    const src=this.portrait.matches?video.dataset.portVideo:video.dataset.landVideo;
    if(src&&video.getAttribute('src')!==src){video.src=src;video.load();next.classList.remove('is-playing');}
  }
  later(callback, delay) { this.introTimers.push(setTimeout(() => { if (this.introActive) callback(); }, delay)); }
  startOpening(manual = false) {
    if (this.introActive || this.quiet() || !this.dialog || typeof this.dialog.showModal !== 'function') return;
    this.restoreFocus = document.activeElement;
    this.introActive = true;
    document.documentElement.classList.add('bc-entry-opening');
    this.focused = false;
    this.select(0);
    const dialog = this.dialog;
    const width = window.innerWidth, height = window.innerHeight;
    const w = Math.min(width * .30, height * .32, 340), ratio = 1267 / 1258;
    const x = (width - w) / 2, y = height * .44 - w * ratio / 2;
    dialog.style.setProperty('--mark-width', `${w}px`);
    dialog.style.setProperty('--mark-x', `${x}px`);
    dialog.style.setProperty('--mark-y', `${y}px`);
    dialog.style.setProperty('--word-width', `${Math.min(w * 1.25, width * .7)}px`);
    dialog.style.setProperty('--word-y', `${y + w * ratio + 25}px`);
    dialog.dataset.phase = 'ready';
    dialog.querySelectorAll('.bc-entry-wordmark path').forEach((letter, i) => { letter.style.animationDelay = `${.26 + i * .04}s`; });
    const path = dialog.querySelector('.bc-entry-mark path').getAttribute('d');
    const mask = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1258 1267"><path fill="black" fill-rule="evenodd" d="${path}"/></svg>`;
    dialog.style.setProperty('--b-mask', `url("data:image/svg+xml,${encodeURIComponent(mask)}")`);
    dialog.style.setProperty('--mask-size', `${w}px ${w * ratio}px`);
    dialog.style.setProperty('--mask-position', `${x}px ${y}px`);
    dialog.dataset.fallback = String(!(CSS.supports('mask-composite', 'exclude') || CSS.supports('-webkit-mask-composite', 'xor')));
    try { dialog.showModal(); document.documentElement.classList.remove('bc-entry-pending'); } catch { this.finishOpening(); return; }
    dialog.querySelector('.bc-entry-skip').focus({ preventScroll: true });
    this.later(() => { dialog.dataset.phase = 'trace'; }, 100);
    this.later(() => { dialog.dataset.phase = 'fill'; }, 1500);
    this.later(() => { dialog.dataset.phase = 'window'; }, 1920);
    this.later(() => {
      const start = performance.now();
      const finalW = Math.hypot(width, height) * .62 / .1085;
      const ax = x + w * .2528, ay = y + w * ratio * .5741;
      const grow = now => {
        if (!this.introActive) return;
        const p = Math.min(1, (now - start) / 1150);
        const ease = p < .5 ? 4 * p ** 3 : 1 - (-2 * p + 2) ** 3 / 2;
        const size = w * (finalW / w) ** ease;
        const anchorX = ax + (width / 2 - ax) * ease, anchorY = ay + (height / 2 - ay) * ease;
        dialog.style.setProperty('--mask-size', `${size}px ${size * ratio}px`);
        dialog.style.setProperty('--mask-position', `${anchorX - size * .2528}px ${anchorY - size * ratio * .5741}px`);
        if (p < 1) this.frame = requestAnimationFrame(grow);
        else this.finishOpening();
      };
      this.frame = requestAnimationFrame(grow);
    }, 2740);
    this.later(() => this.finishOpening(), 4300);
    if (!manual) this.restoreFocus = null;
  }
  finishOpening() {
    this.introTimers.forEach(clearTimeout);
    this.introTimers = [];
    cancelAnimationFrame(this.frame);
    document.documentElement.classList.remove('bc-entry-pending','bc-entry-opening');
    const active = this.introActive;
    this.introActive = false;
    if (this.dialog?.open) this.dialog.close();
    if (active) {
      try { sessionStorage.setItem('bc-work-entry-v1', '1'); } catch { /* Access never depends on storage. */ }
      if (this.restoreFocus?.isConnected) this.restoreFocus.focus({ preventScroll: true });
    }
    this.sync();
  }
  teardown() {
    this.controller?.abort();
    document.documentElement.classList.remove('bc-entry-pending','bc-entry-opening');
    this.observer?.disconnect();
    this.introActive = false;
    this.introTimers.forEach(clearTimeout);
    this.introTimers = [];
    clearTimeout(this.advanceTimer);
    cancelAnimationFrame(this.frame);
    this.progress?.cancel();
    if (this.dialog?.open) this.dialog.close();
    this.slides?.forEach(slide => slide.querySelector('video')?.pause());
  }
  disconnectedCallback() { this.teardown(); super.disconnectedCallback(); }
}
if (!customElements.get('bc-work-entry')) customElements.define('bc-work-entry', BarryWorkEntry);
